import { useEffect, useState } from "react";

// Owner mobile workflow helpers. Authorization NEVER lives here — `user.is_admin` comes from GET /auth/me (backend allowlist).
export const MOBILE_QUERY = "(max-width: 767px)";
export const isMobileViewport = () => typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches;

// Where a fresh login lands (Iter126B): master owner → /owner on phone AND desktop; ADMIN_EMAILS-only admins → /inbox; members → /browse.
export const loginLanding = (user) => (user?.is_master_owner ? "/owner" : user?.is_admin ? "/inbox" : "/browse");

export function useIsMobile() {
  const [mobile, setMobile] = useState(isMobileViewport);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const on = (e) => setMobile(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return mobile;
}

export const X_MAX = 280;
const STALE_CAPTION_MS = 3 * 60 * 1000;
const X_URL_RE = /(?:https?:\/\/|www\.)\S+/i;
const X_DOMAIN_RE = /\bbarefootage\.(?:com|net|org|app)\b/i;
const X_NUM3 = "[+-]?\\d{1,3}\\.\\d{3,}";
const X_NUMANY = "[+-]?\\d{1,3}\\.\\d+";
const X_KW = "(?:lat(?:itude)?|lng|lon(?:gitude)?|gps|coords?|coordinates?)";
const X_DIR_PAIR = "\\d{1,3}\\.\\d+\\s*°?\\s*[NS]\\b\\s*,?\\s*\\d{1,3}\\.\\d+\\s*°?\\s*[EW]\\b";
const X_DMS_ONE = "\\d{1,3}\\s*°\\s*\\d{1,2}\\s*['′]\\s*(?:\\d{1,2}(?:\\.\\d+)?\\s*[\"″])?\\s*[NSEW]";
// Mirrors backend `_X_COORD_PAIR_RE`: signed/plain decimal pairs, keyword-led pairs, directional pairs (36.0866 N, 115.1531 W) and DMS.
const X_COORD_RE = new RegExp(`(?:\\b${X_KW}\\s*[:=]?\\s*${X_NUMANY}\\s*(?:,|;|/|\\s|(?:\\s*${X_KW}\\s*[:=]?\\s*))\\s*(?:\\b${X_KW}\\s*[:=]?\\s*)?${X_NUMANY}\\b)|(?:${X_NUM3}\\s*(?:,|;|/|\\s|(?:\\s*${X_KW}\\s*[:=]?\\s*))\\s*(?:\\b${X_KW}\\s*[:=]?\\s*)?${X_NUM3}\\b)|(?:\\b${X_DIR_PAIR})|(?:\\b${X_DMS_ONE}\\s*,?\\s*${X_DMS_ONE})`, "i");

// Mirrors backend `_x_post_unsafe`: a stored x_post is copy-ready ONLY when it passes every rule (empty list = safe).
export const xPostProblems = (text) => {
  const t = (text || "").trim();
  const out = [];
  if (!t) out.push("empty");
  if (t.length > X_MAX) out.push("too long");
  if (X_URL_RE.test(t)) out.push("URL");
  if (X_DOMAIN_RE.test(t)) out.push("BareFootage link");
  if (X_COORD_RE.test(t)) out.push("GPS coordinates");
  return out;
};

// Ready-state for one owner upload, derived only from the stored record (never regenerates anything).
export const ownerRowState = (m) => {
  const x = (m?.x_post || "").trim();
  if (x) {
    const bad = xPostProblems(x);
    return bad.length ? { key: "failed", label: `Stored X text unsafe (${bad.join(", ")}) · retry`, invalid: true } : { key: "ready", label: "Caption ready" };
  }
  if (m?.media_type === "video") {
    if (m.processing_state === "stalled" || m.processing_state === "failed" || m.video_failed) return { key: "failed", label: "Video processing failed" };
    if (m.processing_state === "processing" || !m.cloud_video_url) return { key: "processing", label: "Processing…" };
  }
  const job = m?.ai_job || {};
  if (job.status === "failed") return { key: "failed", label: "Caption failed" };
  if (job.status === "processing") return { key: "captioning", label: "Caption generating…" };
  const age = Date.now() - new Date(m?.created_at || 0).getTime();
  if (job.status === "queued" && age > STALE_CAPTION_MS) return { key: "failed", label: "Caption not generated" };
  return { key: "captioning", label: "Caption generating…" };
};

export const ownerLocationLabel = (m) => {
  const L = m?.location;
  if (!L) return { key: "none", label: "No GPS in file · Needs Location" };
  const name = L.label || L.display || [L.city, L.region, L.country].filter(Boolean).join(", ");
  const pending = L.provenance !== "owner_selected" && (L.provenance === "original_metadata" || (L.venue_resolution_status && !["resolved", "no_containing_venue"].includes(L.venue_resolution_status)));
  if (pending) return { key: "pending", label: name ? `${name} · resolving place…` : "Location detected · resolving place…" };
  // Iter125y: ONLY the canonical backend state — never precision/coordinates inference
  const key = L.state === "exact" ? "exact" : L.state === "general_confirmed" ? "general" : L.state === "needs_verification" ? "verify" : "none";
  return { key, label: name || "Location detected" };
};

// Clipboard copy with a textarea fallback (older mobile browsers); resolves true on success.
export const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); return true; } catch (e) {}
  try {
    const ta = document.createElement("textarea");
    ta.value = text; ta.setAttribute("readonly", ""); ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select(); ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (e) { return false; }
};
